<template>
  <div>
    <div
      v-if="displayLabel || hasTooltip"
      class="label-tooltip-wrapper"
    >
      <div
        v-if="displayLabel"
        id="text-field-label"
        :style="computedLabelStyle"
        :title="displayLabel"
      >
        {{ displayLabel }}
      </div>

      <v-tooltip
        v-if="hasTooltip"
        v-bind="tooltipProps"
      >
        <!-- Vue 3 + Vuetify 3 migration: change to v-slot:activator="{ props }" and v-bind="props" -->
        <template #activator="{ on, attrs }">
          <v-icon
            class="ml-2"
            :style="computedTooltipIconStyle"
            v-bind="attrs"
            v-on="on"
          >
            {{ computedTooltipIcon }}
          </v-icon>
        </template>

        <template v-if="hasTooltipText">
          <span>{{ computedTooltipText }}</span>
        </template>
        <template v-else-if="computedTooltipCustomComponent">
          <component
            :is="computedTooltipCustomComponent"
            :config="computedTooltipConfig"
          />
        </template>
      </v-tooltip>
    </div>

    <v-text-field
      ref="inputRef"
      v-model="computedTextValue"
      v-bind="computedProps"
      @input="onInput"
      @click="onClick"
      @click:append="onClickAppend"
      @focusin="onFocusIn"
      @focusout="onFocusOut"
      @keydown.enter.prevent="onEnter"
      @click:prepend-inner="onClickPrependInner"
      @click:clear="onClearIconClick"
    >
      <template #append>
        <v-progress-circular
          v-if="computedAppendLoading"
          indeterminate
          size="20"
          width="2"
          color="primary"
        />
        <image-field
          v-else-if="hasAppendImage"
          v-bind="appendImageProps"
          @onClick="onClickAppend"
        />
      </template>

      <template #prepend-inner>
        <image-field
          v-if="hasPrependInnerImage"
          v-bind="prependInnerImageProps"
          @onClick="onClickPrependInner"
        />
      </template>
    </v-text-field>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount, defineExpose } from 'vue'
import {
  getBooleanFromValue,
  isArray,
  isBoolean,
  isEmpty,
  isFunction,
  isObject,
  isSet,
  isString
} from '@/global/utils/utils'
import ImageField from '@/global/components/fields/ImageField.vue'
import { useTranslate } from '@/global/composables/useTranslate'

const { translate } = useTranslate()

// --- Constants ---
const DEFAULT_FIELD_TYPE = 'text'
const DEFAULT_TOOLTIP_COLOR = '#6B7280'
const DEFAULT_TOOLTIP_ICON = 'mdi-information-slab-circle'
const DEFAULT_TOOLTIP_FONT_SIZE = '16px'
const DEFAULT_TOOLTIP_MARGIN_BOTTOM = '1px'
const DEFAULT_ON_INPUT_TIMEOUT = 500
const DEFAULT_FRONTEND_DECIMAL_SEPARATOR = ','
const DEFAULT_BACKEND_DECIMAL_SEPARATOR = '.'
const FOCUS_DELAY_MS = 100

// --- Props & emits ---
const props = defineProps({
  field: { type: Object, default: () => ({}) },
  config: { type: Object, default: () => ({}) },
  value: { type: [Number, String, Object, Array], default: null },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  validationErrors: { type: Object, default: () => ({}) },
  rules: { type: Array, default: () => ([]) },
  collectionKey: { type: String, default: '' },
  collectionIndex: { type: Number, default: 0 },
  updateData: { type: Function, default: () => {} },
  updateValidationErrors: { type: Function, default: () => {} },
  formData: { type: Object, default: () => ({}) },
  autofocus: { type: Boolean, default: false },
  useFieldLabel: { type: Boolean, default: false },
  customFieldKey: { type: String, default: '' },
  removeDataPropertyByKey: { type: Function, default: () => {} },
  replacePropertyValueByKey: { type: Function, default: () => {} },
  fieldClass: { type: String, default: '' },
  editMode: { type: Boolean, default: false },
  createMode: { type: Boolean, default: false },
  // Fields are clearable unless a config/caller says otherwise.
  clearable: { type: Boolean, default: true }
})

const emit = defineEmits([
  'clear-validation-errors',
  'input',
  'click',
  'append-click',
  'prepend-inner-click',
  'onInput',
  'on-enter',
  'focusin',
  'focusout',
  'typing'
])

// --- Template refs & local state ---
const inputRef = ref(null)
const showPassword = ref(false)
const onInputTimeoutId = ref(null)

// --- Config resolution ---
const computedConfig = computed(() => {
  const isValid = (val) => isSet(val) && !isEmpty(val)
  if (isValid(props.field)) {
    return props.field
  }
  if (isValid(props.config)) {
    return props.config
  }
  return {}
})

const fieldProps = computed(() =>
  isSet(computedConfig.value?.props) && isObject(computedConfig.value.props)
    ? computedConfig.value.props
    : {}
)

// --- Read / edit state ---
const dependantReadonly = computed(() =>
  isFunction(fieldProps.value?.dependantReadonly)
    ? fieldProps.value.dependantReadonly({ editState: props.editMode })
    : false
)

const isReadonly = computed(() =>
  props.readonly || !!computedConfig.value.readonly || dependantReadonly.value
)

const canEdit = computed(() => !isReadonly.value && !props.disabled)

// `forceEditStyle` fields (a datetime-picker activator, a "pick from dialog"
// field like the location picker) are always rendered `readonly` - the user
// interacts via a picker/dialog, not by typing - so `canEdit` is always false
// for them even while the form is being edited.
const isEffectivelyEditable = computed(() =>
  canEdit.value || ((props.editMode || props.createMode) && !!computedConfig.value?.forceEditStyle)
)

// Whether a clear icon makes sense at all. A `forceEditStyle` field is always
// `readonly`, so `isEffectivelyEditable` only opens up for it while a form is
// in edit/create mode - but such a field is just as often used standalone (a
// dialog's date input, a filter), with neither flag set, and there the icon
// would never render even though `clearable` defaults to true. Being enabled
// is what makes it interactive in that case; a field that shouldn't be cleared
// is either `disabled` or sets `clearable: false`.
const isClearIconAllowed = computed(() =>
  !props.disabled && (isEffectivelyEditable.value || !!computedConfig.value?.forceEditStyle)
)

const computedEditableProp = computed(() => {
  const { editable = true } = computedConfig.value || {}
  return isBoolean(editable) ? editable : false
})

// --- Decimal helpers ---
// A field config can carry its rules either as the `rules` prop or inside its
// `props` pass-through - FieldsRenderer binds the latter, so both arrive.
const configuredRules = computed(() =>
  isArray(fieldProps.value?.rules) ? fieldProps.value.rules : props.rules
)

const isDecimalField = computed(() => configuredRules.value?.some((r) => r === 'decimal'))

function getDecimalDelimiter () {
  return computedConfig.value?.decimalDelimiter ?? DEFAULT_FRONTEND_DECIMAL_SEPARATOR
}

function toDisplayValue (value) {
  if (isDecimalField.value && value && !isObject(value)) {
    return String(value).replace(
      new RegExp(`\\${DEFAULT_BACKEND_DECIMAL_SEPARATOR}`, 'g'),
      getDecimalDelimiter()
    )
  }
  return value
}

function toStorageValue (value) {
  if (isDecimalField.value && value && !isObject(value)) {
    return String(value).replace(
      new RegExp(`\\${getDecimalDelimiter()}`, 'g'),
      DEFAULT_BACKEND_DECIMAL_SEPARATOR
    )
  }
  return value
}

// --- v-model ---
const computedTextValue = computed({
  get () {
    const { custom_display_value: customDisplayValue } = computedConfig.value?.options || {}
    const formatted = toDisplayValue(props.value)
    return isFunction(customDisplayValue) ? customDisplayValue(formatted) : formatted
  },
  set (newVal) {
    emit('input', toStorageValue(newVal))
  }
})

// --- Label ---
const label = computed(() => {
  const { label: cfgLabel } = computedConfig.value || {}
  return isString(cfgLabel) ? cfgLabel : ''
})

const displayLabel = computed(() => (props.useFieldLabel ? '' : label.value))
const displayFieldLabel = computed(() => (props.useFieldLabel ? label.value : ''))

// --- Validation ---
const computedRules = computed(() => {
  if (props.disabled || isReadonly.value) {
    return []
  }

  const ruleHandlers = {
    email: (value) => {
      // An empty field is nothing to complain about - saying so is `required`'s job,
      // same as with `decimal` below. A field that is filled in from elsewhere (a
      // picked person's contact, say) empties legitimately.
      if (value === undefined || value === null || value === '') {
        return true
      }

      const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return pattern.test(value) || translate('base.enter_valid_email_address')
    },
    required: (value) => !!value || translate('base.this_field_mandatory'),
    decimal: (value) => {
      if (value === undefined || value === null || value === '') {
        return true
      }
      const delimiter = getDecimalDelimiter()
      return new RegExp(`^[0-9]+(${delimiter}[0-9]+)?$`).test(value) ||
        translate('base.valid_number_instructions').replace(':delimiter', `"${DEFAULT_FRONTEND_DECIMAL_SEPARATOR}"`)
    }
  }

  return configuredRules.value.map((rule) =>
    isString(rule) && ruleHandlers[rule] ? ruleHandlers[rule] : rule
  )
})

const computedValidationErrors = computed(() => {
  let messages = []
  const { key, validationPropertyKey, validationErrorIncludeKey } = computedConfig.value || {}

  // Without `isEffectivelyEditable` (see above), a backend validation error on a
  // required-but-not-typed `forceEditStyle` field would silently never render.
  if (!key || isEmpty(props.validationErrors) || !isEffectivelyEditable.value) {
    return messages
  }

  for (const errorKey in props.validationErrors) {
    if (errorKey === key || errorKey === props.customFieldKey) {
      messages = props.validationErrors[errorKey]
    }
    else if (validationPropertyKey && errorKey === validationPropertyKey) {
      messages = props.validationErrors[errorKey]
    }
    else if (validationErrorIncludeKey && errorKey?.includes(validationErrorIncludeKey)) {
      messages = props.validationErrors[errorKey]
    }
    else {
      const collectionBase = `${props.collectionKey}.${props.collectionIndex}`
      if (errorKey === `${collectionBase}.${key}` || errorKey === collectionBase) {
        messages = props.validationErrors[errorKey]
      }
    }
  }

  return messages
})

// --- Styles & classes ---
// `forceEditStyle` lets a field opt into the edit-mode look (green fill/border)
// even when it's read-only — e.g. a field whose only interaction is opening
// a picker on click, where "editing" still needs to read as active/actionable.
const isEditingStyled = computed(() => {
  const { useDefaultEditStyle = true, forceEditStyle = false } = computedConfig.value || {}
  return props.editMode && (canEdit.value || forceEditStyle) && useDefaultEditStyle
})

const fieldStyle = computed(() => {
  const { style: configStyle } = computedConfig.value || {}
  const base = {}
  // Leave the background to the `.text-field--is-editing` CSS class when
  // editing - an inline style here would otherwise force white on the root
  // element, which the class's `.v-input__slot` background rule can't
  // out-specificity-argue its way around since they target different nodes.
  // Fields always stay white otherwise: a non-editable field is communicated by
  // its (muted) text and cursor, not by a grey box, which reads as disabled
  // chrome and made read-only rows look broken next to editable ones.
  if (!isEditingStyled.value) {
    base.backgroundColor = '#FFFFFF'
  }
  return isSet(configStyle) && isObject(configStyle) ? { ...base, ...configStyle } : base
})

const computedFieldClasses = computed(() => {
  const { class: cfgClass, onClick: cfgOnClick } = computedConfig.value || {}
  const classes = []

  if (props.fieldClass) classes.push(props.fieldClass)
  if (isEditingStyled.value) classes.push('text-field--is-editing')
  if (!canEdit.value) classes.push('text-field--readonly')
  if (!computedEditableProp.value && !isReadonly.value) classes.push('cursor-default')
  if (isFunction(cfgOnClick)) classes.push('text-field--clickable')
  if (computedValidationErrors.value.length) classes.push('text-field--has-error')
  if (cfgClass && isString(cfgClass)) classes.push(cfgClass)
  classes.push(`mt-${displayLabel.value ? 1 : 0}`)

  return classes.join(' ')
})

const computedLabelStyle = computed(() => {
  const { labelStyle } = computedConfig.value || {}
  return isSet(labelStyle) && (isObject(labelStyle) || isString(labelStyle)) ? labelStyle : undefined
})

// --- Field type ---
const computedConfigFieldType = computed(() => computedConfig.value?.field_type)

const computedFieldType = computed(() => {
  if (!computedConfigFieldType.value || !isString(computedConfigFieldType.value)) {
    return DEFAULT_FIELD_TYPE
  }
  if (computedConfigFieldType.value === 'password') {
    return showPassword.value ? DEFAULT_FIELD_TYPE : 'password'
  }
  return computedConfigFieldType.value
})

// --- Append / prepend ---
const computedAppendLoading = computed(() => getBooleanFromValue(computedConfig.value?.appendLoading))

const computedAppendIcon = computed(() => {
  if (computedAppendLoading.value) {
    return ''
  }
  const { appendIcon } = computedConfig.value || {}
  if (isString(appendIcon)) {
    return appendIcon
  }
  if (computedConfigFieldType.value === 'password' && canEdit.value) {
    return showPassword.value ? 'mdi-eye' : 'mdi-eye-off'
  }
  return ''
})

const computedPrependInnerIcon = computed(() => {
  const { prependInnerIcon } = computedConfig.value || {}
  return isString(prependInnerIcon) ? prependInnerIcon : undefined
})

const computedPrependIcon = computed(() =>
  isString(computedConfig.value?.prependIcon) ? computedConfig.value.prependIcon : undefined
)

const computedAppendOuterIcon = computed(() =>
  isString(computedConfig.value?.appendOuterIcon) ? computedConfig.value.appendOuterIcon : undefined
)

// --- Suffix / Prefix ---
function resolveStringOrFn (val) {
  if (isString(val)) return val
  if (isFunction(val)) return val()
  return undefined
}

const computedSuffix = computed(() => resolveStringOrFn(computedConfig.value?.suffix))
const computedPrefix = computed(() => resolveStringOrFn(computedConfig.value?.prefix))

// --- Tooltip ---
const tooltip = computed(() => computedConfig.value?.tooltip)
const hasTooltip = computed(() => !!tooltip.value)
const computedTooltipText = computed(() => tooltip.value?.text)
const hasTooltipText = computed(() => !!computedTooltipText.value)
const computedTooltipIcon = computed(() => tooltip.value?.icon || DEFAULT_TOOLTIP_ICON)
const computedTooltipConfig = computed(() => tooltip.value?.config)
const computedTooltipCustomComponent = computed(() => tooltip.value?.component)

const computedTooltipIconStyle = computed(() => {
  const style = tooltip.value?.style
  return isObject(style)
    ? style
    : { color: DEFAULT_TOOLTIP_COLOR, fontSize: DEFAULT_TOOLTIP_FONT_SIZE, marginBottom: DEFAULT_TOOLTIP_MARGIN_BOTTOM }
})

const tooltipProps = computed(() => {
  const raw = isObject(tooltip.value) ? tooltip.value : {}
  const dir = tooltip.value?.direction
  return {
    top: !dir || dir === 'top',
    left: dir === 'left',
    right: dir === 'right',
    bottom: dir === 'bottom',
    ...raw
  }
})

// --- Image slots ---
function buildImageProps (imageConfig) {
  const base = isSet(imageConfig) && isObject(imageConfig) ? imageConfig : {}
  return {
    ...base,
    class: isSet(base.class) && isString(base.class) ? `field-image ${base.class}` : 'field-image'
  }
}

const appendImageConfig = computed(() => computedConfig.value?.append)
const hasAppendImage = computed(() => isString(appendImageConfig.value?.src))
const appendImageProps = computed(() => buildImageProps(appendImageConfig.value))

const prependInnerImageConfig = computed(() => computedConfig.value?.prependInner)
const hasPrependInnerImage = computed(() => isString(prependInnerImageConfig.value?.src))
const prependInnerImageProps = computed(() => buildImageProps(prependInnerImageConfig.value))

// --- Misc field props ---
const computedPlaceholder = computed(() => {
  const { placeholder } = computedConfig.value || {}
  return isString(placeholder) && (props.editMode || props.createMode) ? placeholder : ''
})

const computedHint = computed(() => {
  const { hint } = computedConfig.value || {}
  return isString(hint) ? hint : undefined
})

const computedFieldName = computed(() => computedConfig.value?.name || computedConfig.value?.key)

const computedFieldAutocompleteProp = computed(() => computedConfig.value?.autocomplete || 'on')

const computedAutofocus = computed(() =>
  getBooleanFromValue(computedConfig.value?.autofocus) || props.autofocus
)

// --- All v-text-field props bundled ---
const computedProps = computed(() => ({
  class: computedFieldClasses.value,
  type: computedFieldType.value,
  dense: true,
  flat: true,
  outlined: true,
  label: displayFieldLabel.value,
  autofocus: computedAutofocus.value,
  hideDetails: 'auto',
  placeholder: computedPlaceholder.value,
  readonly: isReadonly.value,
  disabled: props.disabled,
  errorMessages: computedValidationErrors.value,
  errorCount: computedValidationErrors.value.length || computedRules.value.length,
  hint: computedHint.value,
  name: computedFieldName.value,
  autocomplete: computedFieldAutocompleteProp.value,
  // `??`, not `||`: now that the default is `true`, an explicit
  // `clearable: false` in the field config has to be able to win. Vuetify draws
  // the clear icon on readonly inputs too (and it stays clickable there), so the
  // field also has to actually be interactive for it to make sense.
  clearable: (computedConfig.value?.clearable ?? props.clearable) && isClearIconAllowed.value,
  appendIcon: computedAppendIcon.value,
  prependInnerIcon: computedPrependInnerIcon.value,
  suffix: computedSuffix.value,
  prefix: computedPrefix.value,
  prependIcon: computedPrependIcon.value,
  appendOuterIcon: computedAppendOuterIcon.value,
  style: fieldStyle.value,
  ...fieldProps.value,
  // After the spread on purpose. `props` carries rules as shorthand strings
  // ('email', 'required'), and Vuetify renders a non-function rule as a
  // permanent error message - so the mapped validators have to win over the raw
  // config value, whichever of the two paths it arrived through.
  rules: computedRules.value
}))

// --- Shared callback context ---
function getFieldContext () {
  return {
    updateData: props.updateData,
    updateValidationErrors: props.updateValidationErrors,
    removeDataPropertyByKey: props.removeDataPropertyByKey,
    replacePropertyValueByKey: props.replacePropertyValueByKey,
    formData: props.formData,
    collectionKey: props.collectionKey,
    collectionIndex: props.collectionIndex
  }
}

// --- Watchers ---
watch(
  () => computedConfig.value?.inputConfig,
  (config) => {
    const { class: inputClass } = config || {}
    nextTick(() => {
      const el = inputRef.value?.$el?.querySelector('input')
      if (inputClass && el) {
        el.classList.add(inputClass)
      }
    })
  },
  { immediate: true }
)

watch(
  computedTextValue,
  (value) => {
    clearTimeout(onInputTimeoutId.value)
    const { onInput: cfgOnInput, clearErrorsOnValueChange } = computedConfig.value || {}

    if (value && clearErrorsOnValueChange === true) {
      emit('clear-validation-errors')
    }

    if (value) {
      emit('onInput', getFieldContext())
    }

    if (isFunction(cfgOnInput)) {
      onInputTimeoutId.value = setTimeout(() => {
        cfgOnInput(value, getFieldContext())
      }, DEFAULT_ON_INPUT_TIMEOUT)
    }
  }
)

onBeforeUnmount(() => clearTimeout(onInputTimeoutId.value))

// --- Event handlers ---
function onClick (event) {
  const { onClick: cfgOnClick } = computedConfig.value || {}
  if (isFunction(cfgOnClick)) {
    cfgOnClick({ event, editMode: props.editMode, createMode: props.createMode, formData: props.formData })
  }
  emit('click', event)
}

function onInput (val) {
  emit('clear-validation-errors')
  const { onTyping, allowLeadingZero = true, blockNegative = false } = computedConfig.value || {}

  if (computedFieldType.value === 'number' && isString(val)) {
    let sanitized = val
    if (blockNegative) {
      sanitized = sanitized.replace(/-/g, '')
    }
    if (!allowLeadingZero) {
      sanitized = sanitized.replace(/^(?:0+(?=[1-9])|0+(?=0$))/g, '')
    }
    if (sanitized !== val) {
      nextTick(() => emit('input', sanitized))
    }
  }

  const payload = { value: val, ...getFieldContext() }
  emit('typing', payload)
  if (isFunction(onTyping)) {
    onTyping(payload)
  }
}

function onFocusIn (event) {
  const { onFocusIn: cfgOnFocusIn } = computedConfig.value || {}
  emit('focusin', { event })
  if (isFunction(cfgOnFocusIn)) {
    cfgOnFocusIn({ event, textValue: computedTextValue.value })
  }
}

function onFocusOut (event) {
  const { onFocusOut: cfgOnFocusOut } = computedConfig.value || {}
  emit('focusout', { event })
  if (isFunction(cfgOnFocusOut)) {
    cfgOnFocusOut({ event, textValue: computedTextValue.value })
  }
}

function onEnter (event) {
  const { onEnterPress } = computedConfig.value || {}
  const payload = { event, textValue: computedTextValue.value }
  if (isFunction(onEnterPress)) {
    onEnterPress(payload)
  }
  emit('on-enter', payload)
}

function onClickAppend () {
  const { appendIconClick } = computedConfig.value || {}
  emit('append-click')
  if (computedConfigFieldType.value === 'password') {
    showPassword.value = !showPassword.value
  }
  if (isFunction(appendIconClick)) {
    appendIconClick({
      ...getFieldContext(),
      editMode: props.editMode,
      createMode: props.createMode,
      value: computedTextValue.value
    })
  }
}

function onClickPrependInner () {
  emit('prepend-inner-click')
}

function onClearIconClick () {
  const { onClearIconClick: cfgOnClearIconClick } = computedConfig.value || {}
  if (isFunction(cfgOnClearIconClick)) {
    cfgOnClearIconClick()
  }
}

// --- Public API ---
function focus () {
  nextTick(() => {
    const el = inputRef.value?.$el?.querySelector('input')
    if (el) {
      setTimeout(() => el.focus(), FOCUS_DELAY_MS)
    }
  })
}

defineExpose({ focus })
</script>

<style lang="scss" scoped>
$field-font-family: 'Satoshi', sans-serif;
$field-font-size: 14px;
$field-line-height: 20px;
$field-color-default: #111827;
$field-color-muted: #6B7280;

@mixin field-input-base {
  font-family: $field-font-family;
  font-size: $field-font-size !important;
  line-height: $field-line-height !important;
  color: $field-color-default !important;
  font-weight: 500 !important;

  @media (max-width: 959px) {
    font-size: 16px !important;
  }
}

@mixin field-input-muted {
  font-family: $field-font-family;
  font-size: $field-font-size !important;
  line-height: $field-line-height !important;
  color: $field-color-muted !important;
  font-weight: 400 !important;

  @media (max-width: 959px) {
    font-size: 16px !important;
  }
}

::v-deep {
  .v-input > .v-input__control > .v-input__slot > .v-text-field__slot > input {
    @include field-input-base;
  }

  // Disabled fields keep the normal text colour: they are mostly used to render
  // an already-filled value the user isn't editing right now (a view mode, or a
  // value derived from another field), and greying that out reads as "empty" or
  // "broken" rather than "not editable". Vuetify's own theme greys disabled
  // inputs out, so the base rule above needs its `!important` to win.
  .v-input--is-disabled > .v-input__control > .v-input__slot > .v-text-field__slot > input {
    @include field-input-base;
  }

  .text-field--readonly .v-input__control > .v-input__slot > .v-text-field__slot > input {
    @include field-input-muted;
  }

  // `forceEditStyle` fields (eg. a datetime-picker activator or a "pick from
  // dialog" field) stay `readonly` even while editing, but should still read
  // as active/editable text, not the muted read-only look above.
  .text-field--readonly.text-field--is-editing .v-input__control > .v-input__slot > .v-text-field__slot > input {
    @include field-input-base;
  }

  .v-input > .v-input__control > .v-input__slot > .v-text-field__slot > .v-label {
    font-family: $field-font-family;
    font-size: $field-font-size;
    color: $field-color-default;
    line-height: $field-line-height;
    font-weight: 500;
  }

  .v-input > .v-input__control > .v-input__slot > .v-input__prepend-inner > .v-input__icon > .v-icon {
    color: rgba(0, 0, 0, 0.38);
  }

  .v-input > .v-input__append-outer > .v-input__icon > .v-icon {
    color: $field-color-default !important;
    font-size: 15px !important;
  }

  .v-text-field--outlined fieldset {
    border-color: #E5E7EB !important;
    border-radius: 8px !important;
  }

  .text-field--is-editing fieldset {
    border-color: #65AD45 !important;
  }

  .cursor-default .v-input__control > .v-input__slot > .v-text-field__slot > input {
    cursor: default;
  }

  .cursor-default .v-input__control > .v-input__slot {
    cursor: default;
  }

  .text-field--clickable .v-input__control > .v-input__slot,
  .text-field--clickable .v-input__control > .v-input__slot > .v-text-field__slot > input {
    cursor: pointer;
  }

  // Scoped to the input box itself (`.v-input__slot`), not the whole
  // `.v-input` - that also contains `.v-text-field__details` (the
  // validation message row below), which would otherwise get painted
  // green too and look like the field stretched down over the error text.
  .text-field--is-editing > .v-input__control > .v-input__slot {
    background-color: #F7FEE7 !important;
  }

  // Placed after the default/editing border rules above so it wins the
  // (equal-specificity) cascade regardless of edit state.
  .text-field--has-error fieldset {
    border-color: #DC2626 !important;
  }
}

#text-field-label {
  font-family: $field-font-family;
  font-size: $field-font-size;
  color: $field-color-default;
  line-height: $field-line-height;
  font-weight: 500;
  // A label never wraps: a second line pushes the input down and knocks the
  // field out of line with its neighbours in the same row. What doesn't fit is
  // cut with an ellipsis and stays readable through the `title` on hover.
  // `min-width: 0` is what lets it shrink at all - it's a flex item inside
  // `.label-tooltip-wrapper`, and flex items don't shrink past their content
  // width on their own.
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-image:hover {
  cursor: pointer;
}

.label-tooltip-wrapper {
  display: flex;
}
</style>
